// 明信片详情（EP-008 扩展 / P-POSTCARD-RECONCEIVE v3 / B10）。
// 单击 PostcardListView 中某条 → 渲染完整合成图（含照片框 + 手写文字）。
// 关闭 → 触发 onClose → 父级 (SpaceView) 复位 selectedId。
//
// 设计：图未到 → img.onerror 降级到 .postcard-detail__placeholder 灰块（任务硬约束，不能崩游戏）。
//
// 🆕 STEP 5-D：**已读标记不在本组件处理**。
//   改由 SpaceView 在列表项点击回调里**同步**调 markPostcardRead(id)（先落盘 → 再开详情）。
//   原因：挂在 mount useEffect 上依赖「组件真挂载 → React 跑 effect」的调度时序，
//   真浏览器里（StrictMode 双挂载 / 列表重渲染 / entry 复用）不可靠；
//   改到点击处同步触发后，时序确定、与渲染状态解耦。本组件退化为纯渲染（无副作用、无 store 依赖）。
//
// 🆕 v3.0：明信片图片已包含完整内容（照片框 + 手写文字），不再叠加分离文字。
//   stamp/body 仍从 POSTCARD_NOTE_BY_ID 读取，仅用于 aria-label，不渲染文字。
//
// 注意：本文件位于 src/views，纯渲染组件。
import { useState } from 'react';
import type { PostcardEntry } from '../../save/types';
import { POSTCARD_NOTE_BY_ID } from '../../runtime/postcardNotes';
import { POSTCARD_ASSET_BY_NOTE, assetIndex, type AssetId } from '../../assets/assetIndex';

interface PostcardDetailProps {
  entry: PostcardEntry;
  onClose: () => void;
}

export function PostcardDetail({ entry, onClose }: PostcardDetailProps) {
  const note = POSTCARD_NOTE_BY_ID[entry.noteId];
  const stamp = note?.stamp ?? entry.destinationId;
  const body = note?.body ?? '（明信片内容暂不可读）';
  // 🆕 v2.1：按 noteId 查图（12 张每张独立），不再按 destination 共享一张图。
  // noteId 来自 PostcardEntry（经 sanitize 校验），理论上必为已知 key；防御性取 ?? 兜底。
  const assetId = (POSTCARD_ASSET_BY_NOTE as Record<string, AssetId>)[entry.noteId]
    ?? 'postcard_riverside_01';
  const src = assetIndex[assetId];
  // onerror 降级到占位灰块（任务硬约束：图未到时不能崩游戏）
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div
      className="postcard-detail"
      role="dialog"
      aria-modal="true"
      aria-label={`明信片详情：寄自 ${stamp}。${body}`}
      tabIndex={-1}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="postcard-detail__panel">
        <div className="postcard-detail__media">
          {imgFailed ? (
            <div className="postcard-detail__placeholder" aria-label="明信片图片暂未到">
              <span>{stamp}</span>
            </div>
          ) : (
            <img
              className="postcard-detail__img"
              src={src}
              alt=""
              aria-hidden="true"
              loading="lazy"
              onError={() => setImgFailed(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
